import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

const ADMIN_EMAIL = 'stan@greenvision.energy';

export type MapProvider = 'mapbox' | 'leaflet';

export function useMapProvider() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const isAdmin = user?.email === ADMIN_EMAIL;

  const { data: mapProvider = 'leaflet', isLoading } = useQuery({
    queryKey: ['app-settings', 'map_provider'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'map_provider')
        .single();
      
      if (error) {
        console.error('Error fetching map provider:', error);
        return 'leaflet' as MapProvider;
      }
      
      return (data?.value as MapProvider) || 'leaflet';
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const updateMutation = useMutation({
    mutationFn: async (newProvider: MapProvider) => {
      if (!isAdmin) {
        throw new Error('Only admin can update map provider');
      }
      
      const { error } = await supabase
        .from('app_settings')
        .upsert({ 
          key: 'map_provider', 
          value: newProvider,
          updated_at: new Date().toISOString(),
          updated_by: user?.id
        });
      
      if (error) throw error;
      return newProvider;
    },
    onSuccess: (newProvider) => {
      queryClient.setQueryData(['app-settings', 'map_provider'], newProvider);
    },
  });

  const setMapProvider = (provider: MapProvider) => {
    if (isAdmin) {
      updateMutation.mutate(provider);
    }
  };

  return {
    mapProvider: mapProvider as MapProvider,
    setMapProvider,
    isAdmin,
    isLoading,
    isUpdating: updateMutation.isPending,
  };
}
